import { create, StoreApi, Mutate, UseBoundStore } from 'zustand';
import { devtools, persist, PersistOptions } from 'zustand/middleware';
import { devTools } from './devtools';
import { withImmer } from './withImmer';
import { StateCreator } from './zustand';
/**
 * Helper function that creates a Zustand store that allows persisting store's data using any kind of storage.
 * it’s useful to persist a Zustand store, so we don’t lose its state if a user refreshes the website or
 * comes back later.
 * Furthermore, it integrates the store with Redux Devtools and implements immer middleware that allows working with
 * immutable state in a more convenient way.
 * @param config - Store configuration method
 * @param persistOptions - config object for persist middleware.
 * @param options - redux devtools options
 * @returns A Zustand store integrated persist middleware, Redux Devtools and immer.
 */

type AdditionalMethods = {
  /**Resets the store to the initial state*/
  reset: () => void;
};

type StoreReturnType<T> = UseBoundStore<
  Mutate<StoreApi<T>, [['zustand/persist', unknown], ['zimmer', never], ['zustand/devtools', never]]>
> &
  AdditionalMethods;

const resetSet = new Set<AdditionalMethods['reset']>();

export const createStoreWithPersist = <T>(
  config: StateCreator<T, [['zustand/persist', unknown], ['zimmer', never], ['zustand/devtools', never]]>,
  persistOptions: PersistOptions<T>,
  options: Parameters<typeof devtools>[1]
): StoreReturnType<T> => {
  const store = create<T>()(
    persist(withImmer(devTools(config, { ...(options ?? {}), store: options?.name })), persistOptions)
  ) as StoreReturnType<T>;

  const initialState = store.getState();

  const reset = () => {
    store.setState(initialState, true, 'RESET_STORE');
  };
  resetSet.add(reset);
  store.reset = reset;
  return store;
};

export const resetStoreWithPersist = () => resetSet.forEach((reset) => reset());
